import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react'
import { usePaletteTheme } from '../../../context/paletteTheme'
import { Group } from '@visx/group'
import { BarGroup } from '@visx/shape'
import { AxisBottom, AxisLeft } from '@visx/axis'
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale'
import { withParentSize } from '@visx/responsive'
import { GridRows } from '@visx/grid'
import Bar from './Bar'
import GraphLegend from '../GraphLegend'
import AxisBottomText from './AxisBottomText'
import BarGroupTooltip from './BarGroupTooltip'
import styles from './BarGroup.module.scss'

const GROUP_PADDING = 0.2
const BAR_PADDING = 0.1

const getNumber = (value) => Number(typeof value === 'undefined' ? 0 : value)

const BarGroupGraph = ({
    formatValue,
    axisButtonProps,
    CastumeAxisBottom,
    CastumeAxisLeft,
    keys,
    colors,
    xKey,
    data,
    parentWidth,
    parentHeight,
    margin = { top: 50, right: 0, bottom: 50, left: 10 },
    showLegend,
    topValue,
    lowValue,
    showAxisLeft,
    showValueOnHover,
    showValue,
  }) => {
  const [ graphProps, setGraphProps ] = useState([])
  const [ choosenBar, setChoosenBar ] = useState(null)
  const ref = useRef(null)

  const { colors: themeColors } = usePaletteTheme()

  useEffect(() => {
    const props = keys.map((key, i) => ({name:key, color: colors[i], display:'area'}))
    setGraphProps(props.reverse())
    return () => {}
  },[keys, colors])

  const handleLegendClick = (i) => {
    const modifiedSettings = [...graphProps]
    const { display } = modifiedSettings[i]
    modifiedSettings[i] = {...modifiedSettings[i], display: display === 'none' ? 'area' : 'none' }
    setGraphProps(modifiedSettings)
  }
  
  const maxValue = topValue ?? Math.max(...data.map(d => Math.max(...keys.map(key => getNumber(d[key])))))
  const minValue = lowValue ?? Math.min(...data.map(d => Math.min(...keys.map(key => getNumber(d[key])))))
  const getXKey = useCallback((d) => d[xKey],[xKey])
  const xScaleDomain = useMemo(() => data.map(getXKey), [data, getXKey])
  const maxBarSize = 300

  const colorScale = scaleOrdinal({
    domain: keys,
    range: colors,
  })

  // scales
  const x0Scale = scaleBand({
    domain: xScaleDomain,
    padding: GROUP_PADDING,
  })

  const x1Scale = scaleBand({
    domain: keys,
    padding: BAR_PADDING,
  })

  let yScale = scaleLinear({
    domain: [0, maxValue],
    range: [0, parentHeight]
  })

  yScale = scaleLinear({
      domain: [minValue < 0 ? minValue : 0, maxValue > 0 ? maxValue : 0],
      range: [parentHeight - yScale([minValue, 0 ])]
  })

  // bounds
  const xMax = parentWidth - margin.left - margin.right
  const yMax = parentHeight - margin.top - margin.bottom - 50

  const sideMargin = useMemo(() => {
    const calcMargin = (xMax - xScaleDomain.length * maxBarSize) / 2
    return calcMargin > 0 ? calcMargin : 0
  },[xMax, xScaleDomain.length])

  // update scale output dimensions
  x0Scale.rangeRound([sideMargin, xMax - sideMargin])
  x1Scale.rangeRound([0, x0Scale.bandwidth()])
  yScale.range([yMax, 0])

  const renderAxisBottom = useMemo(() => (
    <AxisBottom top={yMax + 30} hideTicks scale={x0Scale} hideAxisLine>
      {axisBottom => axisBottom.ticks.map(axis => 
        CastumeAxisBottom  ? CastumeAxisBottom({...axis, ...axis.from, maxBarSize}) :
        <AxisBottomText {...axisButtonProps} {...axis} {...axis.from} maxBarSize={maxBarSize} key={axis.value}/>)} 
    </AxisBottom>
 ),[yScale]) // eslint-disable-line

 const renderAxisLeft = useMemo(() => (
    <AxisLeft hideTicks top={-10} hideAxisLine scale={yScale} numTicks={5}>
      {axisBottom => axisBottom.ticks.map(axis => 
      CastumeAxisLeft  ? CastumeAxisLeft({...axis, ...axis.from, maxBarSize}) :
      <AxisBottomText {...axisButtonProps} {...axis} {...axis.from} maxBarSize={maxBarSize} key={axis.value}/>)} 
    </AxisLeft>
 ),[yScale]) // eslint-disable-line

 const grid = useMemo(()=> (
    <GridRows tickValues={[0]} scale={yScale} width={xMax} height={yMax}>
      {grid => {
        const { from, to } = grid.lines[0]
        return <line ref={ref} x1={from.x} y1={from.y} x2={to.x} y2={to.y} stroke={themeColors.divider} />
      }}
    </GridRows>
 ),[yScale, xMax, yMax, themeColors.divider])

 const zeroPosition = useMemo(() => ref?.current && Number(ref.current.attributes.y1.value),[grid]) // eslint-disable-line

 const handleMouseMove = (bar) => setChoosenBar(bar)
 const onMouseLeave = () => setChoosenBar(null)

  return (
    <div className={styles['group-bar']}>
      {showLegend && <GraphLegend handleClick={handleLegendClick} layers={graphProps} margin={margin}/>}
      <svg width={parentWidth} height={parentHeight}>
        <Group top={margin.top} left={margin.left}>
          {grid}
          {renderAxisBottom}
          {showAxisLeft && renderAxisLeft}   
          <BarGroup
            data={data}
            keys={keys}
            height={yMax}
            x0={getXKey}
            x0Scale={x0Scale}
            x1Scale={x1Scale}
            yScale={yScale}
            color={colorScale}
          >
            {barGroups => barGroups.map((barGroup, barGroupIndex) => (
              <Group key={`bar-group-${barGroup.index}-${barGroup.x0}`} left={barGroup.x0}>
                {barGroup.bars.map((bar, barIndex) => graphProps.find(prop => prop.name === bar.key)?.display !== 'none'  
                  && <Bar padding={{bar: BAR_PADDING, group: GROUP_PADDING}} formatValue={formatValue} showValue={showValue} x0={barGroup.x0} handleMouseMove={handleMouseMove} onMouseLeave={onMouseLeave} key={`bar-${barGroup.index}-${barIndex}`} bars={barGroup.bars} bar={bar} data={data} index={barGroupIndex} x1Scale={x1Scale} yScale={yScale}/>
                )}
              </Group>))}
          </BarGroup>
        </Group>
      </svg>
      {showValueOnHover && choosenBar && <BarGroupTooltip choosenBar={choosenBar} xKey={xKey} formatValue={formatValue} zeroPosition={zeroPosition}/>}
    </div>
  )
}

export default withParentSize(BarGroupGraph)